import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BottomNav from '../../components/BottomNav';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Star, Clock, Gift, Crown, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Explore = () => {
  const [merchants, setMerchants] = useState([]);
  const [stats, setStats] = useState({ merchants: 0, members: 0, cities: 12 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedMerchant, setSelectedMerchant] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [merchantsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/network/merchants`),
        axios.get(`${API_URL}/api/network/stats`)
      ]);
      setMerchants(merchantsRes.data.merchants || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load network data:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'Tous' },
    { id: 'cafe', label: 'Cafés' },
    { id: 'restaurant', label: 'Restaurants' },
    { id: 'retail', label: 'Boutiques' },
    { id: 'salon', label: 'Salons' },
  ];

  const getInitials = (name) => {
    return name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'HB';
  };

  const filteredMerchants = filter === 'all' 
    ? merchants 
    : merchants.filter(m => m.business_type === filter);

  const featuredMerchant = merchants.length > 0 
    ? merchants.reduce((prev, curr) => (prev.rating > curr.rating ? prev : curr))
    : null;

  return (
    <div className="min-h-screen bg-[#18261E] pb-24">
      {/* Header */}
      <div className="px-5 pt-8 pb-6">
        <p className="text-[#C3A365] text-xs tracking-widest uppercase mb-1">Réseau</p>
        <h1 className="text-2xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {stats.merchants} établissements
        </h1>
      </div>

      {/* Filters - Simple pills */}
      <div className="px-5 mb-6">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setFilter(cat.id)}
              className={`px-4 py-2 text-sm whitespace-nowrap transition-all rounded-full ${
                filter === cat.id
                  ? 'bg-[#C3A365] text-[#18261E] font-medium'
                  : 'text-[#C3A365] border border-[#C3A365]/30'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Featured */}
      {featuredMerchant && filter === 'all' && (
        <div className="px-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Crown size={14} className="text-[#C3A365]" weight="fill" />
            <span className="text-[#C3A365] text-xs tracking-widest uppercase">Coup de cœur</span>
          </div>
          
          <motion.div
            onClick={() => setSelectedMerchant(featuredMerchant)}
            className="bg-[#1E2F26] rounded-2xl p-5 border border-[#C3A365]/20 cursor-pointer"
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-[#C3A365] flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-semibold text-[#18261E]">{getInitials(featuredMerchant.business_name)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-medium truncate">{featuredMerchant.business_name}</h3>
                  <span className="text-[#C3A365] text-sm">★ {featuredMerchant.rating}</span>
                </div>
                <p className="text-[#8BA89A] text-sm mb-3">{featuredMerchant.description?.slice(0, 60)}...</p>
                <div className="flex items-center gap-2 text-[#C3A365] text-xs">
                  <Gift size={14} />
                  <span>{featuredMerchant.visits_for_reward} visites → {featuredMerchant.reward_description}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Merchants List - Clean layout */}
      <div className="px-5">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#1E2F26] rounded-xl p-4 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#283D32] rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-[#283D32] rounded w-2/3" />
                    <div className="h-3 bg-[#283D32] rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredMerchants
              .filter(m => filter === 'all' ? m.id !== featuredMerchant?.id : true)
              .map((merchant, index) => (
              <motion.div
                key={merchant.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => setSelectedMerchant(merchant)}
                className="bg-[#1E2F26] rounded-xl p-4 cursor-pointer active:bg-[#243528] transition-colors"
              >
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-lg bg-[#283D32] flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-medium text-[#C3A365]">{getInitials(merchant.business_name)}</span>
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-white font-medium truncate pr-2">{merchant.business_name}</h3>
                      <span className="text-[#C3A365] text-sm flex-shrink-0">★ {merchant.rating}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[#8BA89A] text-xs">
                      <span>{merchant.address?.split(',')[0]}</span>
                      <span>•</span>
                      <span>{merchant.hours}</span>
                    </div>
                  </div>
                  
                  <ArrowRight size={16} className="text-[#8BA89A] flex-shrink-0" />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedMerchant && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={() => setSelectedMerchant(null)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="absolute bottom-0 left-0 right-0 bg-[#1E2F26] rounded-t-3xl max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                {/* Handle */}
                <div className="w-10 h-1 bg-[#3E5248] rounded-full mx-auto mb-6" />
                
                {/* Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 rounded-xl bg-[#C3A365] flex items-center justify-center">
                    <span className="text-xl font-semibold text-[#18261E]">{getInitials(selectedMerchant.business_name)}</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl text-white font-medium mb-1">{selectedMerchant.business_name}</h2>
                    <div className="flex items-center gap-2 text-[#8BA89A] text-sm">
                      <span>★ {selectedMerchant.rating}</span>
                      <span>•</span>
                      <span className="capitalize">{selectedMerchant.business_type}</span>
                    </div>
                  </div>
                </div>
                
                {/* Description */}
                <p className="text-[#8BA89A] mb-6 leading-relaxed">{selectedMerchant.description}</p>
                
                {/* Info */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-[#8BA89A]">
                    <MapPin size={18} className="text-[#C3A365]" />
                    <span className="text-sm">{selectedMerchant.address}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#8BA89A]">
                    <Clock size={18} className="text-[#C3A365]" />
                    <span className="text-sm">{selectedMerchant.hours}</span>
                  </div>
                </div>
                
                {/* Reward */}
                <div className="bg-[#283D32] rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-2 text-[#C3A365] mb-2">
                    <Gift size={18} />
                    <span className="text-sm font-medium">Récompense fidélité</span>
                  </div>
                  <p className="text-white">
                    {selectedMerchant.visits_for_reward} visites = <span className="text-[#C3A365] font-medium">{selectedMerchant.reward_description}</span>
                  </p>
                </div>
                
                {/* CTA */}
                <button
                  onClick={() => {
                    toast.success('Scannez le QR code sur place !');
                    setSelectedMerchant(null);
                  }}
                  className="w-full bg-[#C3A365] text-[#18261E] py-4 rounded-xl font-semibold"
                >
                  Voir l'itinéraire
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
};

export default Explore;
